/*  ========================================================================
    # Sections - Badges
    ========================================================================  */

import React, { useRef } from 'react';
import type { BadgesProps } from '.';
import { motion, useScroll, useTransform } from 'motion/react';
import useBreakpoints from '../../hooks/useBreakpoints';

const Badges = ({ content, badges }: BadgesProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const breakpoints = useBreakpoints();
  const totalBadges = badges.length;
  const segmentSize = 1 / totalBadges;

  const { scrollYProgress } = useScroll({
    target: scrollRef,
    offset: ['start start', 'end end']
  });

  const getSettings = (index: number) => {
    if (breakpoints.lg === false) {
      return {
        fadeSettings: [1],
        opacitySettings: [1],
        translateSettings: [0]
      };
    } else {
      const segmentStart = (index * segmentSize) * 1.15;
      const segmentEnd = (index + 1) * segmentSize;

      const fadeSettings = [0, Math.max(0, segmentStart - 0.15), segmentStart, segmentEnd, Math.min(1, segmentEnd + 0.15), 1];
      const opacitySettings = [0.3, 0.3, 1, 1, 0.3, 0.3];
      const translateSettings = [40, 40, 0, 0, 40, 40];

      if (index === badges.length - 1) {
        opacitySettings[4] = 1;
        opacitySettings[5] = 1;
        translateSettings[4] = 0;
        translateSettings[5] = 0;
      }

      return {
        fadeSettings,
        opacitySettings,
        translateSettings
      };
    }
  };

  return (
    <div ref={scrollRef}>
      <div className='s-badges__sticky-container'>
        <div className='container'>
          <div className='row tw-w-full tw-items-center'>
            <div className='col tw-w-full lg:tw-w-5/12' style={{ position: 'sticky', top: '0', alignSelf: 'flex-start', zIndex: 10 }}>
              <div className='s-badges__content'>
                <div className='wysiwyg-block' dangerouslySetInnerHTML={{ __html: content }} />
              </div>
            </div>
            <div className='col tw-w-full lg:tw-w-7/12 xl:tw-ml-[calc(1/12*100%)] xl:tw-w-6/12'>
              <div className='s-badges__badges-wrapper'>
                {(() => {
                  return (
                    <motion.div
                      className='s-badges__badges-container'
                    >
                      {badges.map((badge, index) => {
                        const { fadeSettings, opacitySettings, translateSettings } = getSettings(index);

                        const opacity = useTransform(
                          scrollYProgress,
                          fadeSettings,
                          opacitySettings
                        );

                        const translateX = useTransform(
                          scrollYProgress,
                          fadeSettings,
                          translateSettings
                        );

                        return (
                          <motion.div
                            key={`${index}-badge`}
                            className={`s-badges__badge ${badge.svg ? ' s-badges__badge--has-icon' : ''}`}
                            style={{ opacity, x: translateX }}
                          >
                            <div className='s-badges__badge-content'>
                              <div className={`s-badges__badge-icon s-badges__badge-icon--${badge.color}`} dangerouslySetInnerHTML={{ __html: badge.svg }}>
                              </div>
                              <div
                                className={`s-badges__badge-text s-badges__badge-text--${badge.color} wysiwyg-block`}
                                dangerouslySetInnerHTML={{ __html: badge.content }}
                              />
                            </div>
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Badges;
